import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OneColumnGroupDto,
  TwoColumnGroupDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  InlineAlert,
  OneColumnGroup,
  TwoColumnGroup,
  Widget,
} from '@asset-metrix/portal-ui';

import { isNull } from '../../utils/nullish';
import { PageContentError } from '../page/PageContentError';
import { Table } from '../table/Table';
import { VegaChart } from '../vegachart/VegaChart';

import { DraftDashboardTile } from './utils/dashboard.types';
import { regroupDashboardTilesToContents } from './dashboardLayoutHelpers';
import { isHydratedAvailable, isUnavailableTile } from './dashboardTileGuards';
import { useDashboardContentEditActions } from './useDashboardContentEditActions';

import pageContentErrorStyles from '../page/pageContentError.module.scss';

type DashboardContentRendererProps = {
  tiles: readonly DraftDashboardTile[];
  sourcePageId: string;
  dashboardId: string;
};

function DashboardPlaceholderTile({
  dashboardId,
  instanceId,
  title,
}: {
  dashboardId: string;
  instanceId: string;
  title: string;
}) {
  const { t } = useTranslation();
  const actions = useDashboardContentEditActions(dashboardId, instanceId, []);

  return (
    <Widget title={title} actions={actions} dataTestId="dashboard-placeholder">
      <InlineAlert className={pageContentErrorStyles['root_content_error']}>
        {t('app.dashboard.placeholder.pendingHydration')}
      </InlineAlert>
    </Widget>
  );
}

function DashboardUnavailableTile({
  dashboardId,
  instanceId,
}: {
  dashboardId: string;
  instanceId: string;
}) {
  const { t } = useTranslation();
  const actions = useDashboardContentEditActions(dashboardId, instanceId, []);

  return (
    <Widget
      title={t('app.page.pageContentErrorTitle')}
      actions={actions}
      dataTestId="dashboard-unavailable"
    >
      <InlineAlert className={pageContentErrorStyles['root_content_error']}>
        {`${t('app.page.contentNotFoundError')} ${t('app.noAccessError.resolution')}`}
      </InlineAlert>
    </Widget>
  );
}

function DashboardWidget({
  widget,
  tile,
  sourcePageId,
  dashboardId,
}: {
  widget: WidgetDto;
  tile: DraftDashboardTile;
  sourcePageId: string;
  dashboardId: string;
}) {
  if (isUnavailableTile(tile)) {
    return (
      <DashboardUnavailableTile
        dashboardId={dashboardId}
        instanceId={tile.instanceId}
      />
    );
  }

  if (!isHydratedAvailable(tile)) {
    return (
      <DashboardPlaceholderTile
        dashboardId={dashboardId}
        instanceId={tile.instanceId}
        title={widget.title || tile.contentId}
      />
    );
  }

  const entity = widget.entity;
  const common = {
    pageId: sourcePageId,
    instanceId: tile.instanceId,
    dashboardId,
    dashboardContentId: tile.instanceId,
    contentSourcePageId: tile.sourcePageId,
    savedPageFilterSelections: tile.pageFilterSelections,
    savedCustomTableFilterSelections: tile.customTableFilterSelections,
  };

  if (entity.type === 'table') {
    return (
      <Table
        allowOpenInFullscreen={widget.allowOpenInFullscreen}
        collapsible={widget.collapsible}
        id={widget.id}
        title={widget.title}
        infoIcon={widget.infoIcon}
        entity={entity}
        {...common}
      />
    );
  }
  if (entity.type === 'vegaChart') {
    return (
      <VegaChart
        allowOpenInFullscreen={widget.allowOpenInFullscreen}
        collapsible={widget.collapsible}
        id={widget.id}
        title={widget.title}
        infoIcon={widget.infoIcon}
        entity={entity}
        {...common}
      />
    );
  }

  return <PageContentError />;
}

function renderGroupContents(
  group: OneColumnGroupDto | TwoColumnGroupDto,
  placements: DraftDashboardTile[],
  sourcePageId: string,
  dashboardId: string
) {
  let placementIndex = 0;

  const walk = (
    nodes: OneColumnGroupDto['contents'] | TwoColumnGroupDto['contents']
  ): ReactNode[] =>
    nodes.map((node, index) => {
      if (node.type === 'widget') {
        const tile = placements[placementIndex];
        placementIndex += 1;
        if (isNull(tile)) {
          return null;
        }
        return (
          <DashboardWidget
            key={`${sourcePageId}-${tile.instanceId}`}
            widget={node}
            tile={tile}
            sourcePageId={sourcePageId}
            dashboardId={dashboardId}
          />
        );
      }
      if (node.type === 'singleColumnGroup') {
        return (
          <OneColumnGroup key={`ocg-${index}`}>
            {walk(node.contents)}
          </OneColumnGroup>
        );
      }
      return null;
    });

  return walk(group.contents);
}

export function DashboardContentRenderer({
  tiles,
  sourcePageId,
  dashboardId,
}: DashboardContentRendererProps) {
  const rows = regroupDashboardTilesToContents(tiles);

  return (
    <>
      {rows.map((row, index) => {
        const key = `${sourcePageId}-row-${index}-${row.placements
          .map((p) => p.instanceId)
          .join('-')}`;

        if (row.group.type === 'twoColumnGroup') {
          return (
            <TwoColumnGroup key={key}>
              {renderGroupContents(
                row.group,
                row.placements,
                sourcePageId,
                dashboardId
              )}
            </TwoColumnGroup>
          );
        }

        return (
          <Fragment key={key}>
            <OneColumnGroup>
              {renderGroupContents(
                row.group,
                row.placements,
                sourcePageId,
                dashboardId
              )}
            </OneColumnGroup>
          </Fragment>
        );
      })}
    </>
  );
}
